import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { Company, User } from "@core/models";
import { BookedService, CompanyService, NgxToastrService, TrainService, TravelService, UserService, UtilsService } from "@core/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ExpiredModalComponent } from "@shared/component/expired-modal/expired-modal.component";
import moment from "moment";
import { Subscription } from "rxjs";

@Component({
  selector: "app-details",
  templateUrl: "./details.component.html",
  styleUrls: ["./details.component.scss"],
})
export class DetailsComponent implements OnInit, OnDestroy {
  @Input() authorization: any;
  @Input() user: any;
  @Input() fromD: string;
  @Input() toD: string;
  @Output() sendInfo = new EventEmitter<any>();
  @ViewChild("textarea") textarea;
  authorizationUser: User;
  subscriptions: Subscription[] = [];
  travellersWithFFDocuments: { fullName: string, documentNumber: string }[] = [];
  priceFromCheck: number;
  companyFromUser: any;
  authTravel: any;
  formattedTravellers: string = '';
  private expiredModalOpen = false;
  private expiredHandledCorrelationIds = new Set<string>();
  groupedBaggagesInfo: Array<{ route: string; items: string[] }> = [];

  constructor(
    private ngxToastrService: NgxToastrService,
    private translateService: TranslateService,
    private modal: NgbModal,
    private userService: UserService,
    private bookedService: BookedService,
    public utilsService: UtilsService,
    private companyService: CompanyService,
    private travelService: TravelService,
    public trainService: TrainService
  ) {
      this.utilsService.socket.on("check-rate-authorization", (result, err) => {
      const cid = result?.correlationId;

      try {
        const expectedCid = this.authorization?.requestData?.rooms?.[0]?.correlationId;
        if (!cid || !expectedCid) return;
        if (expectedCid !== cid) return;

        this.utilsService.isLoading = false;

        const isExpired =
          result?.data?.statusCode === 817 || result?.data?.errorCode === "TOKEN_EXPIRED";

        if (isExpired) {
          const key = `auth:${cid}`;

          if (this.expiredHandledCorrelationIds.has(key)) return;
          this.expiredHandledCorrelationIds.add(key);

          if (this.expiredModalOpen) return;
          this.expiredModalOpen = true;

          try { this.modal.dismissAll(); } catch {}

          const modalRef = this.modal.open(ExpiredModalComponent, {
            windowClass: "custom-modal-expired",
            centered: true,
            backdrop: "static",
            keyboard: false,
          });

          modalRef.componentInstance.redirectTo = `/`;

          modalRef.result.finally(() => {
            this.expiredModalOpen = false;
            this.expiredHandledCorrelationIds.clear();
          });

          return;
        }

        let paxes = this.authorization.requestData.rooms[0].paxes;

        this.authorization.requestData.rooms[0] = result.data.accommodation.rooms[0].rates
          ? result.data.accommodation.rooms[0]?.rates[0]
          : result.data.accommodation.rooms[0];

        if (!this.authorization?.requestData?.rooms[0]?.paxes) {
          this.authorization.requestData.rooms[0].paxes = paxes;
        }

        if (result.data.price?.totalPrice?.total) {
          this.priceFromCheck = result.data.price?.totalPrice?.total;
        }
      } catch (error) {
        this.utilsService.isLoading = false;
      }
    });
  }

  ngOnInit(): void { 
    if (this.authorization.type === "booking"){
      this.utilsService.isLoading = true;
    }
    if(this.authorization.travelUuid){
      this.travelService.getTravel(this.authorization.travelUuid).subscribe(res => {
        this.authTravel = res;
      });
    }

    this.priceFromCheck = this.authorization?.price || 0;
    if (this.authorization.type.toLowerCase() === "flight") {
      const travellers = this.authorization?.requestData?.query?.travellers;
      if (travellers && Array.isArray(travellers)) {
        travellers.forEach(traveller => {
          const documents = traveller?.documents;
          if (documents && Array.isArray(documents)) {
            const document = documents.find(doc => doc.type === 'FF');
            if (document && document?.number) {
              const travellerName = traveller.name;
              const travellerSurnames = traveller.surnames;
              const documentNumber = document.number;
              
              this.travellersWithFFDocuments.push({
                fullName: `${travellerName} ${travellerSurnames.join(' ')}`,
                documentNumber: documentNumber
              });
            }
          }
        });
      }
      if (this.authorization?.requestData?.query?.travellers?.length > 0) {
        this.formattedTravellers = this.authorization.requestData.query.travellers
          .map(t => t.name + ' ' + (t.surnames?.join(' ') || ''))
          .join(', ');
      }
      this.groupedBaggagesInfo = this.getGroupedBaggagesInfo();
    }
    if (this.authorization?.requestData?.passengers?.length > 0) {
      this.formattedTravellers = this.authorization.requestData.passengers
        .map(t => t.name + ' ' + (t.surname || '') + ' ' + (t.lastSurname || ''))
        .join(', ');
    }

    if (this.authorization.type === "booking") {
      const parsedRoom = {...this.authorization.requestData?.rooms[0]};
      parsedRoom.nameRoom = this.authorization.requestData?.rooms[0].name;
      parsedRoom.net = this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.net || this.authorization.requestData?.rooms?.[0]?.net;
      parsedRoom.rateType = this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.rateType || this.authorization.requestData?.rooms?.[0]?.rateType;
      parsedRoom.rateKey = this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.rateKey || this.authorization.requestData?.rooms?.[0]?.rateKey;
      parsedRoom.cancellation = !!this.authorization.requestData?.rooms?.[0]?.rates?.[0]?.cancellationPolicies
      parsedRoom.cancellationPolicies = this.authorization.requestData?.rooms?.[0]?.rates?.[0].cancellationPolicies
      if(parsedRoom.cancellationPolicies){
        parsedRoom.cancellationPolicies?.forEach((item, index, array) => {
          array[index] = {
            amount: item.amount,
            from: item.from,
            text: item.text,
          }
        });
      }
      const body = {
        userUuid: this.authorization.userUuid,
        checkIn: moment(this.authorization.fromDate).format('YYYY-MM-DD'),
        checkOut: moment(this.authorization.toDate).format('YYYY-MM-DD'),
        rooms: [parsedRoom],
        guests: this.authorization.requestData?.guests,
        isAuthorization: true
      }
      const correlation = crypto.randomUUID();
      this.utilsService.correlationId.push(correlation);
      body['correlationId'] = correlation;
      this.bookedService.checkRate(body).subscribe((rate) => {
        this.authorization.requestData.rooms[0].correlationId = correlation;
      }, (error) => this.utilsService.isLoading = false)
    }
    this.subscriptions.push(
      this.userService.getUser(this.authorization.userUuid).subscribe((user: User) => {
        this.authorizationUser = user;
        if(user.companyUuid){
          this.companyService.getCompany(user.companyUuid).subscribe(
            (company: Company) => {
              this.companyFromUser = company;
            },
            (error) => {}
          )
        }
      })
    )
  }

  getHours(authorization, departure: boolean) {
    let departHour: string;
    let returnHour: string;
    if (authorization.type?.toLowerCase() === "train") {
      departHour = moment(authorization.requestData.departDate).format("HH:mm");
      returnHour =
        authorization.requestData.returnDate != null
          ? moment(authorization.requestData.returnDate).format("HH:mm")
          : "";
    } else {
      if (authorization.requestData.departureDate) {
        departHour = moment(authorization.requestData.departureDate).format("HH:mm");
      } else {
        departHour = authorization.requestData.departDate.split(" ")[1];
      }

      if (authorization.requestData.returnDate && authorization.requestData.returnDate.match(/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/)) {
        returnHour = authorization.requestData.returnDate.split(" ")[1];
      } else {
        returnHour = moment(authorization.requestData.returnDate).format("HH:mm");
      }
    }

    return departure ? departHour : returnHour;
  }

  onDeny(authorization) {
    const action = this.textarea.nativeElement;

    if (!action.value || action.value === "") {
      this.translateService
        .get(["common.error", "authorization.swal.comment-required"])
        .subscribe((transl) => {
          this.ngxToastrService.typeInfo(
            transl["common.error"],
            transl["authorization.swal.comment-required"]
          );
        });
    } else {
      const info = {
        action: action.value,
        deny: true,
        authorization: authorization,
        loading: true
      };
      this.sendInfo.emit(info);
    }
  }

  onAccept(authorization) {
    const action = this.textarea.nativeElement;
    const info = {
      action: action.value,
      deny: false,
      authorization: authorization,
      loading: true,
      authorizationUser: this.authorizationUser,
      companyFromUser: this.companyFromUser,
      authTravel: this.authTravel
    };
    this.sendInfo.emit(info);
  }

  getLogo(value: string) {
    return `assets/img/airlines/small/${value}.png`;
  }

  hasValidScales(authorization: any): boolean {
    return authorization?.requestData?.scales?.some(
      scale => Array.isArray(scale.layovers) && scale.layovers.length > 0
    );
  }

  getBaggagesInfo(): any[] {
    return this.authorization?.requestData?.query?.ancillaries?.filter(
      (item: any) => item.category === 'Baggage' && item.short_name
    ) || [];
  }

  getPriorityInfo(): any[] {
    return this.authorization?.requestData?.query?.ancillaries?.filter(
      (item: any) => item.category === 'PriorityBoard' && item.short_name
    ) || [];
  }

  getGuestsFullNames(): string[] {
    return this.authorization?.requestData?.guests?.map(
      (guest: any) => `${guest.name} ${guest.surname}`
    ) || [];
  }

  getPassengerNameByUuid(uuid: string): string | null {
    const traveller = this.authorization?.requestData?.query?.travellers?.find(t => t.uuid === uuid);
    if (!traveller) return null;

    const surnames = traveller.surnames?.join(' ') || '';
    return `${traveller.name} ${surnames}`.trim();
  }

  getSeatsInfoFromQuery() {
    const seats = this.authorization?.requestData?.query?.seats || [];

    const groupedByPassenger = seats.reduce((acc, seat) => {
      const uuid = seat.uuid;
      if (!acc[uuid]) {
        acc[uuid] = {
          passengerUuid: uuid,
          seats: [],
        };
      }
      acc[uuid].seats.push(seat);
      return acc;
    }, {} as Record<string, { passengerUuid: string; seats: any[] }>);

    return Object.values(groupedByPassenger);
  }

  getGroupedBaggagesInfo(): Array<{ route: string; items: string[] }> {
    const ancillaries = this.authorization?.requestData?.query?.ancillaries;

    if (!Array.isArray(ancillaries) || ancillaries.length === 0) {
      return [];
    }

    const baggages = ancillaries.filter(
      (item: any) => item?.category === "Baggage" && item?.short_name
    );

    if (!baggages.length) {
      return [];
    }

    const grouped = new Map<string, Set<string>>();

    for (const baggage of baggages) {
      const departure = baggage?.departure?.trim();
      const arrival = baggage?.arrival?.trim();

      const route =
        departure && arrival
          ? `${departure} → ${arrival}`
          : baggage?.legType === "outbound"
          ? this.translateService.instant("train.confirm-stepper-container.seat-selection.outward")
          : baggage?.legType === "return"
          ? this.translateService.instant("train.confirm-stepper-container.seat-selection.return")
          : this.translateService.instant("train.form-train.unspecified-route");

      if (!grouped.has(route)) {
        grouped.set(route, new Set<string>());
      }

      grouped.get(route)?.add(baggage.short_name);
    }

    return Array.from(grouped.entries()).map(([route, items]) => ({
      route,
      items: Array.from(items),
    }));
  }

  close() {
    this.modal.dismissAll();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.utilsService.socket.off('check-rate-authorization');
  }

  getCarriers() {
    const totalCarriers = this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain.sections.map((l) => l.carrier)
    if (this.authorization.requestData.trainsInfo.selectedTrains.selectedReturnTrain) {
      this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain.sections.forEach((l) => totalCarriers.push(l.carrier))
    }
    return totalCarriers
  }

  get trips() {
    return [
      {
        type: 'outward',
        carriers: this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain?.sections?.map(l => l.carrier) || [],
        sections: this.authorization.requestData.trainsInfo.selectedTrains.selectedOutwardTrain?.sections || []
      },
      {
        type: 'return',
        carriers: this.authorization.requestData.trainsInfo.selectedTrains.selectedReturnTrain?.sections?.map(l => l.carrier) || [],
        sections: this.authorization.requestData.trainsInfo.selectedTrains.selectedReturnTrain?.sections || []
      }
    ].filter(t => t.carriers.length);
  }
}
